// The live FilterState store, held ABOVE the router (design doc §4's
// persistence-across-navigation rule): a "last 30 days" pick made on one
// dashboard must carry forward when the reader clicks into another, not
// reset on every link click. A plain module-level store rather than a new
// state-management dependency — `useSyncExternalStore` (built into React
// 19) is all a single shared value needs; `zustand` is already a workspace
// dependency elsewhere but pulling it in here for one value would be
// ceremony this doesn't need.
//
// `/d/$slug`'s own `?filters=` search param is the SHAREABLE SNAPSHOT of
// this store (serialized via filter-state.ts's serializeFilterState), not
// its source of truth — the dashboard page seeds the store from the URL on
// a fresh load / shared link and writes the store back to the URL on every
// change; see that page for the seed-once-per-navigation logic.
import { useSyncExternalStore } from "react";

import type { FilterState } from "@bi/shared/types";

import { defaultFilterState } from "./filter-state";

let state: FilterState = defaultFilterState();
const listeners = new Set<() => void>();

export function getFilterState(): FilterState {
  return state;
}

export function setFilterState(next: FilterState): void {
  state = next;
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Read/write the shared store, re-rendering the calling component on every
 *  change from ANY consumer (not just this one) — the whole point of a
 *  store that lives above the router. */
export function useFilterState(): readonly [FilterState, (next: FilterState) => void] {
  const snapshot = useSyncExternalStore(subscribe, getFilterState);
  return [snapshot, setFilterState] as const;
}
